import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SearchInput } from "@/components/common/SearchInput";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { relative } from "@/lib/format";
import { ClipboardList, Plus, FileCog } from "lucide-react";
import { toast } from "sonner";

const GENERATE_SHORTCUT_ITEMS = ["Offer letter issued", "Appointment letter issued"];

export default function ChecklistBoard({ type, title, subtitle }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me, can } = useApp();
  const [starting, setStarting] = useState(false);
  const [empId, setEmpId] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { data: checklists, isLoading } = useQuery({ queryKey: ["checklists", type], queryFn: () => get(`/hrms/checklists?type=${type}`) });
  const { data: employees = [] } = useQuery({ queryKey: ["hrms-directory"], queryFn: () => get("/hrms/directory") });

  const start = useMutation({
    mutationFn: (body) => post("/hrms/checklists", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["checklists", type] }); toast.success("Checklist started"); setStarting(false); setEmpId(""); },
  });
  const toggle = useMutation({
    mutationFn: ({ clid, itemId, checked }) => patch(`/hrms/checklists/${clid}/items/${itemId}`, { checked, _actor: me?.id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["checklists", type] }),
  });

  const inProgress = employees.filter((e) => !checklists?.some((c) => c.employee_id === e.id));

  const filteredChecklists = useMemo(() => {
    return (checklists || []).filter((c) => {
      if (search && !c.employee_name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== "all") {
        const status = c.status === "completed" ? "completed" : "in_progress";
        if (status !== statusFilter) return false;
      }
      return true;
    });
  }, [checklists, search, statusFilter]);

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        {can("hrms", "edit") && (
          <Button data-testid="start-checklist-btn" onClick={() => setStarting((s) => !s)}><Plus className="mr-1.5 h-4 w-4" />Start Checklist</Button>
        )}
      </PageHeader>

      {starting && (
        <Section className="mb-4">
          <div className="flex items-end gap-3 p-4">
            <div className="flex-1">
              <Select value={empId} onValueChange={setEmpId}>
                <SelectTrigger data-testid="checklist-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{inProgress.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setStarting(false)}>Cancel</Button>
            <Button size="sm" data-testid="checklist-start-submit" disabled={!empId} onClick={() => start.mutate({ employee_id: empId, type, _actor: me?.id })}>Start</Button>
          </div>
        </Section>
      )}

      {!isLoading && checklists?.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <SearchInput testid="checklist-search" placeholder="Search employee name" value={search} onChange={setSearch} />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="checklist-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {isLoading ? <Skeleton className="h-64" /> : !checklists?.length ? (
        <EmptyState icon={ClipboardList} title={`No ${type} checklists yet`} />
      ) : !filteredChecklists.length ? (
        <EmptyState icon={ClipboardList} title="No matching checklists" subtitle="Adjust your search or filter." />
      ) : (
        <div className="space-y-4">
          {filteredChecklists.map((c) => {
            const done = c.items.filter((i) => i.checked).length;
            return (
              <Section key={c.id} title={c.employee_name} action={<StatusBadge family={c.status === "completed" ? "green" : "blue"} label={c.status === "completed" ? "Completed" : "In Progress"} />}>
                <div className="border-b border-border/60 px-4 py-3">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{done} / {c.items.length} complete</span>
                    <span>Started {relative(c.started_at)}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${(done / c.items.length) * 100}%` }} /></div>
                </div>
                <div className="divide-y divide-border">
                  {c.items.map((it) => (
                    <label key={it.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                      <Checkbox data-testid={`checklist-item-${it.id}`} checked={it.checked}
                        onCheckedChange={(v) => toggle.mutate({ clid: c.id, itemId: it.id, checked: !!v })} />
                      <span className={it.checked ? "text-muted-foreground line-through" : ""}>{it.label}</span>
                      {can("hrms", "edit") && GENERATE_SHORTCUT_ITEMS.includes(it.label) && (
                        <Button size="sm" variant="ghost" className="ml-auto h-7 px-2 text-xs" data-testid={`checklist-generate-${it.id}`}
                          onClick={(e) => { e.preventDefault(); navigate(`/hrms/documents/generate?entityType=employee&entityId=${c.employee_id}`); }}>
                          <FileCog className="mr-1 h-3.5 w-3.5" />Generate
                        </Button>
                      )}
                    </label>
                  ))}
                </div>
              </Section>
            );
          })}
        </div>
      )}
    </div>
  );
}
